import { IApiAccountType, IApiGroupMappingType, IApiTradingAccountCardInfo } from '@api/interfaces/data-contracts';
import { IApiTradingAccountCardInfoExt } from '@components-member/TradingAccounts/interfaces';
import { GENERAL_CONSTANTS } from 'components/shared/general.constants';
import {
  pushAccountCardDepositClickEvent,
  pushAuthorizedBankingMethodEvent,
  pushDepositPaymentSourceEvent,
  pushDepositPreStartEvent,
  pushErrorDepositEvent,
  pushQuickDepositClickBtnEvent,
  pushSelectDepositFundsCategoryEvent,
  pushSuccessDepositEvent,
} from 'components/shared/googleAnalyticsEvents';
import { getClientSourceType } from 'components/shared/utils/getClientSourceType';
import { FormValue } from 'components/user/psp/psp.interfaces';
import { CURRENCY, TRADING_ACCOUNT_FORM_TYPE, TRANSFER_ACCOUNT_TYPE } from 'global.enums';
import { BaseAnalyticsEventInfo, ClientSourceType } from 'global.interfaces';
import { getAccountCategoryType } from 'utils/getAccountCategoryType';

const walletOperationCategory = (selectedCategory: string): string | undefined => {
  return (GENERAL_CONSTANTS.WALLET_OPERATION_CATEGORY as Array<{ name: string; value: string }>).find(
    (category) => category.value === selectedCategory,
  )?.value;
};

type AccountIdHash = {
  accountIdHash: string;
};

type DepositFundsType = {
  depositFundsType: string;
};

type DepositInfo = {
  depositValue: number;
  depositAmountUSD: FormValue;
  depositCurrency: CURRENCY;
  depositSource: string;
};

interface SendSuccessDataLayerEvent extends DepositInfo, AccountIdHash {
  depositIdHash: string;
  userIdHash: string;
  clientSourceType: ClientSourceType;
  ibUserId: string | null;
  depositFundsType: string;
  verification: string;
  accountType: string;
}

type DepositPaymentSourceAndFunds = BaseAnalyticsEventInfo & DepositFundsType;

export const successDepositEvent = ({
  depositIdHash,
  depositValue,
  depositAmountUSD,
  depositCurrency,
  accountIdHash,
  depositSource,
  userIdHash,
  clientSourceType,
  ibUserId,
  accountType,
  depositFundsType,
  verification,
}: SendSuccessDataLayerEvent): void => {
  pushSuccessDepositEvent(
    accountIdHash,
    depositIdHash,
    depositValue,
    depositAmountUSD as number,
    depositCurrency,
    depositSource,
    userIdHash,
    getClientSourceType(clientSourceType),
    ibUserId,
    accountType,
    depositFundsType,
    verification,
  );
};

interface SendErrorDataLayerEvent extends AccountIdHash {
  errorMessage: string;
}

export const errorDepositEvent = ({ errorMessage, accountIdHash }: SendErrorDataLayerEvent): void => {
  pushErrorDepositEvent(accountIdHash, errorMessage);
};

interface DepositPreStartEvent extends BaseAnalyticsEventInfo, DepositInfo, DepositFundsType {
  accountIdHash: string;
  accountType: string;
}

export const depositPreStartEvent = ({
  userIdHash,
  ibUserId,
  clientSourceType,
  depositSource,
  depositValue,
  depositCurrency,
  depositAmountUSD,
  depositFundsType,
  verification,
  accountIdHash,
  accountType,
}: DepositPreStartEvent): void => {
  pushDepositPreStartEvent(
    userIdHash,
    getClientSourceType(clientSourceType),
    ibUserId,
    accountIdHash,
    accountType,
    depositValue,
    depositCurrency,
    depositAmountUSD as number,
    depositSource,
    depositFundsType,
    verification,
  );
};

export const quickDepositClickBtnEvent = ({
  userIdHash,
  clientSourceType,
  ibUserId,
  verification,
}: BaseAnalyticsEventInfo): void => {
  pushQuickDepositClickBtnEvent(userIdHash, getClientSourceType(clientSourceType), ibUserId, verification);
};

export interface AccountCardDepositClickEvent extends BaseAnalyticsEventInfo {
  event: string;
  account: IApiTradingAccountCardInfo;
}

export const accountCardDepositClickEvent = ({
  event,
  userIdHash,
  clientSourceType,
  verification,
  account,
  ibUserId,
}: AccountCardDepositClickEvent): void => {
  const accountType = getAccountType({
    accountType: account.type.accountType as IApiAccountType,
    groupMappingType: (account as IApiTradingAccountCardInfoExt).groupMappingType,
    investorId: (account as IApiTradingAccountCardInfoExt).investor?.investorId || null,
    masterId: (account as IApiTradingAccountCardInfoExt).master?.masterId || null,
  });

  pushAccountCardDepositClickEvent(
    userIdHash,
    getClientSourceType(clientSourceType),
    ibUserId,
    (account as IApiTradingAccountCardInfoExt).idHash,
    accountType,
    verification,
    event,
  );
};

export const selectDepositFundsCategoryEvent = ({
  depositFundsType,
  userIdHash,
  clientSourceType,
  ibUserId,
  verification,
}: DepositPaymentSourceAndFunds): void => {
  pushSelectDepositFundsCategoryEvent(
    userIdHash,
    getClientSourceType(clientSourceType),
    ibUserId,
    verification,
    walletOperationCategory(depositFundsType),
  );
};

interface DepositPaymentSourceEvent extends DepositPaymentSourceAndFunds {
  depositSource: string;
}

export const depositPaymentSourceEvent = ({
  depositFundsType,
  depositSource,
  userIdHash,
  clientSourceType,
  ibUserId,
  verification,
}: DepositPaymentSourceEvent): void => {
  pushDepositPaymentSourceEvent(
    userIdHash,
    getClientSourceType(clientSourceType),
    ibUserId,
    verification,
    walletOperationCategory(depositFundsType),
    depositSource,
  );
};

interface OnlineBankTransactionEvent extends DepositPaymentSourceAndFunds {
  eventAction: string;
}

export const authorizedBankingMethodEvent = ({
  userIdHash,
  clientSourceType,
  ibUserId,
  verification,
  eventAction,
  depositFundsType,
}: OnlineBankTransactionEvent): void => {
  const bankingTypes = {
    PreAuthorized: 'fast_bank',
    PostTransaction: 'single_transact',
  } as Record<string, string>;

  pushAuthorizedBankingMethodEvent(
    userIdHash,
    getClientSourceType(clientSourceType),
    ibUserId,
    depositFundsType,
    verification,
    bankingTypes[eventAction],
  );
};

type GetAccountType = (accountInfo: GetAccountTypeProps) => TRADING_ACCOUNT_FORM_TYPE | string;

type GetAccountTypeProps = {
  accountType: IApiAccountType | TRANSFER_ACCOUNT_TYPE;
  groupMappingType: IApiGroupMappingType;
  investorId: number | null;
  masterId: number | null;
};

export const getAccountType: GetAccountType = (accountInfo) => {
  const { accountType } = accountInfo;

  if (accountType === IApiAccountType.Demo) {
    return TRADING_ACCOUNT_FORM_TYPE.DEMO;
  }

  const { isMasterAccount, isInvestorAccount, isCopyTradingAccount } = getAccountCategoryType(accountInfo);

  if (!isCopyTradingAccount) {
    return TRADING_ACCOUNT_FORM_TYPE.SELF;
  }

  if (isInvestorAccount) {
    return TRADING_ACCOUNT_FORM_TYPE.INVESTOR;
  }

  if (isMasterAccount) {
    return TRADING_ACCOUNT_FORM_TYPE.MASTER;
  }

  return '';
};
